'use client';

/**
 * Renders ONE ordered timeline segment.
 *
 * Deliberately a plain function, not a component: it is called from inside
 * `ChatMessageItem`'s `blocks.map` and returns the element WITH its key, so the
 * rendered tree is byte-identical to the pre-split version and the order
 * guarantee in `apps/hub/tests/meridian/chat-message-item-order.test.tsx`
 * cannot drift.
 */

import React from 'react';
import { MemoizedMarkdown } from '../../MemoizedMarkdown';
import { ToolApprovalCard } from '../ToolApprovalCard';
import { ToolBlock } from '../ToolBlock';
import { TurnActivityBlock } from '../TurnActivityBlock';
import { PlanCard } from '../PlanCard';
import { ReasoningBlock } from '../ReasoningBlock';
import { ArtifactCard } from '../../artifacts/ArtifactCard';
import type { TimelineSegment } from '../../utils/messageTimeline';
import { getAuthToken } from '../../../lib/api/authenticated-fetch';
import { CHAT_LAYOUT } from '../../../styles/chat-layout';

/** Everything a segment needs from the message row around it. */
export interface SegmentRenderContext {
  messageId: string;
  messageIndex: number;
  isUser: boolean;
  previewData: Record<string, Record<string, unknown> | undefined>;
  previewLoading: Record<string, boolean>;
  previewError: Record<string, boolean>;
  onFetchPreview: (
    toolName: string,
    toolInput: unknown,
    approvalId: string,
    apiBaseUrl: string
  ) => Promise<void>;
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
  isBusy: boolean;
  apiBaseUrl: string;
}

export function renderSegment(
  segment: TimelineSegment,
  index: number,
  isLive: boolean,
  ctx: SegmentRenderContext
): React.ReactNode {
  const idPrefix = `${ctx.messageId}-${ctx.messageIndex}`;

  switch (segment.kind) {
    case 'text':
      // A user's own message is echoed VERBATIM, not parsed as markdown: they
      // typed plain text, and the bubble's `whitespace-pre-wrap` is what keeps
      // the line breaks they actually pressed. Running it through the markdown
      // renderer (which resets whitespace, as it must) would silently join
      // their lines into one paragraph.
      if (ctx.isUser) {
        return (
          <div
            key={segment.key}
            className={`${index > 0 ? 'mt-3 ' : ''}whitespace-pre-wrap break-words text-xs md:text-sm`}
          >
            {segment.text}
          </div>
        );
      }
      return (
        <div
          key={segment.key}
          // Narration and the final answer are separate segments — space them
          // so a tool run reads as a break between them, not a run-on blob.
          // NO `prose` here: @tailwindcss/typography is not loaded in Hub, so
          // `.prose` contributes nothing except a global `.prose a` rule that
          // outranks (higher specificity) the markdown renderer's own link
          // styling. MemoizedMarkdown owns all markdown typography.
          className={`${index > 0 ? 'mt-3 ' : ''}${CHAT_LAYOUT.answerMeasure} text-xs md:text-sm`}
        >
          <MemoizedMarkdown id={`${idPrefix}-${segment.key}`} content={segment.text} />
        </div>
      );

    case 'reasoning':
      return <ReasoningBlock key={segment.key} text={segment.text} isStreaming={isLive} />;

    case 'plan':
      return <PlanCard key={segment.key} {...segment.plan} />;

    case 'artifact':
      return <ArtifactCard key={segment.key} artifact={segment.part.data} />;

    case 'approval': {
      const cacheKey = `${segment.toolName}-${segment.approvalId}`;
      return (
        <ToolApprovalCard
          key={segment.key}
          toolName={segment.toolName}
          part={segment.invocation.toolCall}
          approvalId={segment.approvalId}
          preview={ctx.previewData[cacheKey]}
          isLoading={ctx.previewLoading[cacheKey]}
          hasError={!!ctx.previewError[cacheKey]}
          onFetchPreview={ctx.onFetchPreview}
          apiBaseUrl={ctx.apiBaseUrl}
          onApprove={(id) => ctx.onApprove(id)}
          onDeny={(id) => ctx.onDeny(id)}
          isBusy={ctx.isBusy}
          messageIndex={ctx.messageIndex}
          getAuthToken={getAuthToken}
        />
      );
    }

    case 'tools': {
      // Reached only if a tool run is ever laid out STANDALONE (buildTurnLayout
      // puts every run inside an activity strip today). Kept so a future layout
      // rule cannot silently drop tool work from the transcript.
      if (segment.invocations.length === 1) {
        const invocation = segment.invocations[0]!;
        const part =
          Object.keys(invocation.toolCall).length > 0 ? invocation.toolCall : invocation.result;
        if (!part) return null;
        return (
          <ToolBlock
            key={segment.key}
            part={part}
            pairedResult={invocation.result}
            defaultOpen={false}
          />
        );
      }
      return (
        <TurnActivityBlock
          key={segment.key}
          segments={[segment]}
          isStreaming={isLive}
          idPrefix={idPrefix}
        />
      );
    }

    case 'context':
      return (
        <div key={segment.key} className="mt-2 p-2 bg-opacity-50 rounded border border-current">
          <p className="text-xs font-semibold">Context</p>
          <p className="text-xs">{segment.content}</p>
          {segment.source && (
            <p className="text-xs text-muted-foreground">Source: {segment.source}</p>
          )}
        </div>
      );
  }
}
